import { useEffect } from "react";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Coins, BookOpen, MessageSquare, CalendarDays, Users, GraduationCap, Zap, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ProfileCompletionBanner } from "@/components/common/ProfileCompletionBanner";
import { TeacherSidebar } from "@/components/teacher/dashboard/TeacherSidebar";
import { StudentInquiries } from "@/components/student/dashboard/StudentInquiries";
import { SidebarProvider } from "@/components/ui/sidebar";
import { DashboardTopNav } from "@/components/layout/DashboardTopNav";
import { useTeacherProfile } from "@/contexts/TeacherProfileContext";
import { useTeacherInquiries } from "@/hooks/use-inquiries";
import { NextStepsCard } from "@/components/common/NextStepsCard";
import { useTeacherNextSteps } from "@/hooks/useNextSteps";
import { useDashboardTour } from "@/hooks/useDashboardTour";
import { teacherDashboardTour } from "@/lib/tours/dashboardTours";
import { DashboardAnalyticsCard } from "@/components/teacher/analytics/DashboardAnalyticsCard";
import { GracePeriodBanner } from "@/components/teacher/GracePeriodBanner";
import { TrialCountdownBanner } from "@/components/teacher/TrialCountdownBanner";
import { OfferingNudgeBanner } from "@/components/teacher/OfferingNudgeBanner";
import { useOfferingNudgeBanner } from "@/hooks/useOfferingNudge";
import { GlanceStrip } from "@/components/teacher/dashboard/GlanceStrip";
import { useAuth } from "@/hooks/use-auth-compat";
import { OPEN_THREAD_STATUSES } from "@/services/inquiryService";
import { useClassManagementHub } from "@/hooks/use-class-management";
import { isReleaseFlagOn } from "@/config/featureFlags";
import { cn } from "@/lib/utils";

// ── Helpers ───────────────────────────────────────────────────────────────────

function offeringIcon(type: string) {
  if (type === "ONE_TO_ONE") return <GraduationCap className="w-4 h-4 text-indigo-500" />;
  if (type === "GROUP")      return <Users className="w-4 h-4 text-violet-500" />;
  return <Zap className="w-4 h-4 text-amber-500" />;
}

function formatNextSession(iso: string | null): string {
  if (!iso) return "No upcoming session";
  const d = new Date(iso);
  const now = new Date();
  const diffDays = Math.ceil((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (diffDays === 0) return `Today · ${time}`;
  if (diffDays === 1) return `Tomorrow · ${time}`;
  if (diffDays < 7)  return `${d.toLocaleDateString([], { weekday: "long" })} · ${time}`;
  return `${d.toLocaleDateString([], { month: "short", day: "numeric" })} · ${time}`;
}

function ClassesSummaryCard() {
  const flagOn = isReleaseFlagOn("CLASS_MANAGEMENT");
  const { data, isLoading } = useClassManagementHub();

  if (!flagOn) return null;

  // data is not necessarily an array if the hub endpoint's response shape
  // ever drifts (e.g. a hermetic test's catch-all mock returns `{}`) --
  // guard here the same way GlanceStrip guards its own offerings list.
  const offerings = Array.isArray(data) ? data : [];
  const activeOfferings = offerings.filter(
    (o) => (o.confirmedCount + o.pendingCount) > 0 || o.nextSessionAt,
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between px-0.5">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-600">My Classes</p>
        <Link
          to="/teacher-classes"
          className="flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700 transition-colors"
        >
          View all <ChevronRight className="w-3 h-3" />
        </Link>
      </div>

      {isLoading && (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-16 bg-white rounded-xl border border-slate-200 animate-pulse" />
          ))}
        </div>
      )}

      {!isLoading && activeOfferings.length === 0 && (
        <div className="flex flex-col items-center justify-center text-center py-8 bg-white rounded-xl border border-slate-200 border-dashed">
          <BookOpen className="w-6 h-6 text-slate-300 mb-2" />
          <p className="text-xs font-medium text-slate-500">No active classes yet</p>
          <p className="text-xs text-slate-400 mt-0.5">Enroll students to get started</p>
        </div>
      )}

      {!isLoading && activeOfferings.slice(0, 4).map((offering) => (
        <Link
          key={offering.offeringId}
          to={`/teacher-classes/${offering.offeringId}`}
          className="flex items-start gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3 hover:border-indigo-200 hover:shadow-sm active:scale-[0.98] active:bg-slate-50 transition-all group"
        >
          <div className="mt-0.5 shrink-0">{offeringIcon(offering.offeringType)}</div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-800 truncate group-hover:text-indigo-700 transition-colors">
              {offering.title}
            </p>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <span className="flex items-center gap-1 text-xs text-slate-500">
                <Users className="w-3 h-3" />
                {offering.confirmedCount} student{offering.confirmedCount !== 1 ? "s" : ""}
                {offering.pendingCount > 0 && (
                  <span className="ml-1 text-amber-600 font-medium">+{offering.pendingCount} pending</span>
                )}
              </span>
            </div>
            <p className="flex items-center gap-1 text-xs text-indigo-600 mt-0.5">
              <CalendarDays className="w-3 h-3 shrink-0" />
              {formatNextSession(offering.nextSessionAt)}
            </p>
          </div>
        </Link>
      ))}

      {!isLoading && offerings.length > 4 && (
        <Link
          to="/teacher-classes"
          className="text-center text-xs font-medium text-slate-500 hover:text-indigo-600 transition-colors py-1"
        >
          +{offerings.length - 4} more classes
        </Link>
      )}
    </div>
  );
}

export default function TeacherDashboard() {
  usePageTitle('Teacher Dashboard | BookMyCoaching');
  const { teacherProfile, profileResponse, draftStatus, hasActiveVersion, isLoading } = useTeacherProfile();
  const auth = useAuth();
  const accessToken = (auth.user?.access_token as string | undefined) ?? "";
  const { data: inquiriesData } = useTeacherInquiries(0, 10);
  const nextSteps = useTeacherNextSteps();
  const offeringNudge = useOfferingNudgeBanner();
  useDashboardTour("teacher", teacherDashboardTour, nextSteps.isReady && !isLoading);

  const coinBalance = profileResponse?.profile?.coinBalance ?? null;

  useEffect(() => {
    const welcome = sessionStorage.getItem('role_switch_welcome');
    if (welcome) {
      sessionStorage.removeItem('role_switch_welcome');
      toast.success(`Welcome! You're now also a ${welcome === 'TEACHER' ? 'Teacher' : 'Student'}.`);
    }
  }, []);

  useEffect(() => {
    const coinWelcome = sessionStorage.getItem('teacher_coin_bonus_welcome');
    if (coinWelcome && coinBalance != null) {
      sessionStorage.removeItem('teacher_coin_bonus_welcome');
      toast.success(`Welcome! You've received ${coinBalance} BMC coins.`);
    }
  }, [coinBalance]);

  const teacherName = teacherProfile?.fullName || "";
  const teacherAvatar = teacherProfile?.profileImage || "";
  const teacherSubjects: string[] = (teacherProfile?.subjects || []).map((s: any) =>
    typeof s === "string" ? s : s?.subjectName || s?.subject?.name || s?.name || ""
  ).filter(Boolean);

  const completedFields = {
    about: !!(
      teacherProfile?.fullName &&
      teacherProfile?.phone &&
      teacherProfile?.teachingModes?.length > 0 &&
      teacherProfile?.subjects?.length > 0
    ),
    description: !!(teacherProfile?.professionalBio && teacherProfile?.teachingApproach),
    qualification: !!((teacherProfile as any)?.highestQualification),
    photo: !!teacherProfile?.profileImage,
    video: !!(teacherProfile?.introVideo?.status === 'READY'),
  };

  const inquiries = inquiriesData?.content ?? [];
  const unreadCount = inquiries.filter(i => (i as any).unreadCount > 0).length;
  const openCount = inquiries.filter(i => OPEN_THREAD_STATUSES.has((i as any).status)).length;

  // First name for greeting
  const firstName = teacherName.split(" ")[0] || "";

  return (
    <div className="min-h-screen bg-background teacher-theme">
      <SidebarProvider>
        <DashboardTopNav role="teacher" />
        <div className="min-h-screen flex w-full pt-14">
          <TeacherSidebar
            teacherName={teacherName}
            teacherAvatar={teacherAvatar}
            subjects={teacherSubjects}
          />

          <div className="flex-1 flex flex-col min-w-0">

            {/* ── Page header ── */}
            <div className="px-4 sm:px-6 pt-5 pb-4 border-b border-slate-100 shrink-0 flex items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-slate-900">
                  {teacherName ? `Welcome back, ${teacherName}` : "Dashboard"}
                </h1>
                <p className="text-sm text-slate-600 mt-0.5">Here's what's happening with your teaching</p>
              </div>

              {/* Quick stats */}
              <div className="flex items-center gap-3 shrink-0 flex-wrap justify-end">
                {coinBalance != null && (
                  <Link
                    to="/teacher-coins"
                    className="flex flex-col items-center px-4 py-2 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 shadow-sm hover:shadow-md transition-all"
                  >
                    <div className="flex items-center gap-1.5">
                      <Coins className="w-4 h-4 text-white/80" />
                      <span className="text-lg font-bold text-white">{coinBalance.toLocaleString()}</span>
                    </div>
                    <span className="text-xs text-white/70 font-medium">BMC Coins</span>
                  </Link>
                )}
                {unreadCount > 0 && (
                  <Link
                    to="/teacher-inquiries"
                    className="flex flex-col items-center px-4 py-2 rounded-xl bg-gradient-to-br from-rose-500 to-pink-500 shadow-sm hover:shadow-md transition-all"
                  >
                    <div className="flex items-center gap-1.5">
                      <MessageSquare className="w-4 h-4 text-white/80" />
                      <span className="text-lg font-bold text-white">{unreadCount}</span>
                    </div>
                    <span className="text-xs text-white/70 font-medium">unread</span>
                  </Link>
                )}
              </div>
            </div>

            {/* ── Today at a glance ── */}
            {accessToken && (
              <GlanceStrip
                accessToken={accessToken}
                unreadCount={unreadCount}
                coinBalance={coinBalance}
              />
            )}

            {/* ── Main content ── */}
            <main className="flex-1 overflow-auto bg-slate-50">
              <div className="p-4 md:p-6 space-y-5 max-w-6xl">

                <TrialCountdownBanner />

                <GracePeriodBanner />

                <ProfileCompletionBanner
                  completedFields={completedFields}
                  setupPath="/teacher-profile-setup"
                  draftStatus={draftStatus}
                  hasActiveVersion={hasActiveVersion}
                  isLoading={isLoading}
                />

                {offeringNudge.show && <OfferingNudgeBanner />}

                <NextStepsCard nextSteps={nextSteps} />

                {/* Analytics + Classes row */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                  <div className="lg:col-span-2">
                    <DashboardAnalyticsCard className="border-0 shadow-sm bg-gradient-to-br from-indigo-50 to-violet-50" noDivider />
                  </div>

                  {/* Class hub summary */}
                  <ClassesSummaryCard />
                </div>

                {/* Recent inquiries */}
                <StudentInquiries inquiries={inquiries} />

              </div>
            </main>
          </div>
        </div>
      </SidebarProvider>
    </div>
  );
}
